import '../../theme/all.scss';

import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import styled from 'styled-components/macro';
import { ToastsContainer, ToastsContainerPosition, ToastsStore } from 'react-toasts';
import { Route, Switch, Redirect, useLocation, useHistory } from 'react-router';
import 'typeface-open-sans';
import TagManager from 'react-gtm-module';
import { AgeComplianceContainer } from '../AgeComplianceContainer/AgeComplianceContainer';
import { HeaderBar } from '../HeaderBar/HeaderBar';
import ScrollToTop from './ScrollToTop';
import { Splash } from '../Splash/Splash/Splash';

import StraplezSplash from '../Splash/Splash/StraplezSplash';
import { NetworkSplash } from '../Splash/Splash/NetworkSplash';
import Internal from '../Internal/Internal';
import AffiliateHeader from '../AffiliateHeader/AffiliateHeader';
import NewTagDialog from '../TagDialog/NewTagDialog';
import { addDashesToUUID } from 'src/shared/converters';
import { load as loadUser } from '../../redux/modules/auth';
import redirectHomepages from 'src/shared/constants/redirectHomepages';
import { useSelector, useSettingsSelector } from '../../redux';
import GlobalStyle from './GlobalStyle';
import { AuthFrame } from './AuthFrame';
import { isPrerender } from '../../utils/prerenderDetect';
import { PopupsManager } from '../AdsZones/PopupsManager';
import { HeaderProvider } from '../HeaderBar/HeaderProvider';
import { LangChangeObserverComponent } from 'src/client/components/observers/LangChangeObserverComponent';
import { HTMLHead } from './HTMLHead';
import { useAnalytics } from 'src/client/helpers/useAnalytics';
import { getDeviceType } from 'src/client/utils/getDeviceType';
import { CategoryIds, MeasurementTypes } from 'src/shared/constants/userMeasurement';
import { AdultContentDisclaimerBanner } from '../../components/banners/AdultContentDisclaimerBanner';
import { getUserTypeAsString } from 'src/client/helpers/getUserType';
import { useShowSfwSite } from 'src/client/helpers/useFeatureFlags';
import { StaticSplash } from 'src/client/pages/StaticSplash/StaticSplash';
import { usePartnerBannerCookieManager } from 'src/client/components/PartnerBanners/usePartnerBannerCookieManager';
import { useExternalScript } from 'src/client/helpers';
import { EXOCLICK_COOKIE, TRAFFIC_JUNKY_COOKIE } from 'src/shared/constants/cookies';

if (!isPrerender) {
  require('../../theme/non-prerender.scss');
}

const Div = styled.div`
  background-color: ${(props) => props.theme.primary2};
  color: ${(props) => props.theme.primary4};

  textarea,
  input:not(.btn) {
    background: ${(props) => props.theme.inputBGColor};
    border: 1px solid ${(props) => props.theme.primary3};
    color: ${(props) => props.theme.inputTextColor};
  }

  .form-control {
    color: ${(props) => props.theme.inputTextColor};
  }

  .content {
    margin: 10px auto;
    padding-bottom: 15px;
    background-color: ${(props) => props.theme.primary1};

    @media (max-width: 767px) and (min-width: 640px) {
      z-index: 1;
    }
  }

  img.c-monster {
    position: fixed;
    bottom: 0;
    right: 0;
    display: none;
  }

  .dropdown-menu {
    background: ${(props) => props.theme.primary1};
    border-color: ${(props) => props.theme.primary3};
  }

  .dropdown-item {
    font-size: 0.9rem;
    display: block;
    text-decoration: none;
    padding: 3px 11px;
    //color: ${(props) => props.theme.primary4};

    :hover {
      background-color: ${(props) => props.theme.primary3};
      text-decoration: none;
    }
  }

  .dropdown-menu > li > a:focus,
  .dropdown-menu > li > a:hover,
  .dropdown-menu > li > span:hover,
  .dropdown-item.active {
    color: ${(props) => props.theme.primary5};
    background-color: ${(props) => props.theme.primary3};
    text-decoration: none;
  }

  .dropdown-text,
  .dropdown-menu > li > a,
  .drop-menu > li > span {
    color: ${(props) => props.theme.primary5};
  }
`;

//SEO_CLEANUP
// removing duplicate urls with a trailing slash
const RemoveTrailingSlash = ({ children }) => {
  const location = useLocation();
  const fullPath = location.pathname + location.search + location.hash;
  if (fullPath !== '/' && fullPath.endsWith('/')) {
    const pathWithoutTrailingSlash = fullPath.slice(0, -1);
    return <Redirect to={pathWithoutTrailingSlash} />;
  }

  return children;
};

const splashPageTypeMap = {
  static: StaticSplash,
  straplez: StraplezSplash,
  network: NetworkSplash,
} as const;

export const App = () => {
  usePartnerBannerCookieManager();

  const location = useLocation();
  const { user, loaded: userLoaded, userType } = useSelector((state) => state.auth);
  const { isEU, country } = useSelector((state) => state.auth.location);
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const history = useHistory();
  const showSfwSite = useShowSfwSite();
  const { config, googleTagManager, googleAnalytics, oneTrustDomainID, version } = useSelector((state) => state.app);

  useExternalScript(
    config.externalScripts?.exoclick?.script,
    {
      'data-goal': config.externalScripts?.exoclick?.app?.goal,
      id: config.externalScripts?.exoclick?.app?.id,
    },
    {
      requiredCookies: [EXOCLICK_COOKIE],
    },
  );

  useExternalScript(
    undefined,
    {
      content: config.externalScripts?.trafficJunky?.meta?.content,
      'http-equiv': config.externalScripts?.trafficJunky?.meta?.httpEquiv,
      id: config.externalScripts?.trafficJunky?.meta?.id,
    },
    {
      requiredCookies: [TRAFFIC_JUNKY_COOKIE],
      tagName: config.externalScripts?.trafficJunky?.meta?.tagName,
      target: config.externalScripts?.trafficJunky?.meta?.target,
    },
  );

  // This hook is used to measure our free/guest members, and their conversion rates
  useAnalytics({
    deviceType: getDeviceType(),
    type: MeasurementTypes.View,
    categoryId: CategoryIds.PageViewsAndNewSubscriptions,
  });

  const homepage = useSettingsSelector('homepage');
  const dispatch = useDispatch();

  // strip out `siteUserLogin` query param so that it doesn't cause a user to be redirected to main site by accident
  if (query.has('siteUserLogin')) {
    query.delete('siteUserLogin');

    const querystring = query.toString();
    history.replace(`${location.pathname}${querystring ? `?${querystring}` : ''}`);
  }

  useEffect(() => {
    if (user) {
      window.dataLayer.push({ event: 'pageview' });
    }
    window.dataLayer.push({
      event: 'pageview',
      user_type: getUserTypeAsString(userType),
    });
  }, [user, userType]);

  useEffect(() => {
    dispatch(loadUser());
  }, [dispatch]);

  useEffect(() => {
    if (userLoaded) {
      try {
        const htmlTag = document.documentElement;
        htmlTag.classList.remove('loading');
      } catch {
        // do nothing
      }
    }
  }, [userLoaded]);

  useEffect(() => {
    if (!isPrerender && location.hash.includes('&login')) {
      const url = document.location.href;
      const param = 'login';
      const re = new RegExp(`#.*[?&]${param}=([^&]+)(&|$)`);
      const match = url.match(re);
      const method = match ? match[1] : null;
      if (method) {
        window.dataLayer.push({
          event: 'login',
          method,
        });
      }
    }
  }, [location.hash]);

  useEffect(() => {
    if (!isPrerender && version && userLoaded && googleTagManager) {
      const dataLayer: {
        [key: string]: unknown;
      } = {
        gaTID: googleAnalytics?.UATrackingID,
        ga4TID: googleAnalytics?.['4TrackingID'],
        oneTrustID: oneTrustDomainID,
        appVersion: version,
      };
      if (user) {
        dataLayer.userId = addDashesToUUID(user.UUID);
      }
      TagManager.initialize({
        gtmId: googleTagManager.id,
        dataLayer,
      });
    }
  }, [version, user, userLoaded, googleTagManager, googleAnalytics, oneTrustDomainID]);

  const splashPageType = config.splashPageType as keyof typeof splashPageTypeMap;

  const renderSplashComponent = () => {
    if (!config.splash?.active) {
      return <Redirect to={redirectHomepages[homepage]} />;
    }

    let SplashPageComponent: React.ComponentType = Splash;
    if (splashPageType && splashPageType in splashPageTypeMap) {
      SplashPageComponent = splashPageTypeMap[splashPageType];
    }

    return <SplashPageComponent />;
  };

  return (
    <>
      <Div className={cx({ 'sfw-site': showSfwSite })}>
        <LangChangeObserverComponent />
        <GlobalStyle />
        <ToastsContainer store={ToastsStore} position={ToastsContainerPosition.BOTTOM_CENTER} />
        <ScrollToTop>
          <div className="app" data-country={country} data-is-eu={isEU}>
            <AgeComplianceContainer />

            <AuthFrame />

            <HTMLHead />

            <HeaderProvider>
              <HeaderBar />
              <AffiliateHeader />
            </HeaderProvider>

            <RemoveTrailingSlash>
              <Switch>
                <Route exact path="/" render={renderSplashComponent} />
                <Route component={Internal} />
              </Switch>
            </RemoveTrailingSlash>

            <NewTagDialog />
            <PopupsManager />
          </div>
        </ScrollToTop>
      </Div>
      <AdultContentDisclaimerBanner />
    </>
  );
};
